/**
 * trialService — the web half of the reverse trial's client surfaces (PPL-358).
 *
 * ## One derivation for every countdown
 *
 * The chip, the settings-style row and the premium page must never disagree
 * about how many days are left or how loudly to say it, so the register logic
 * lives here once. Three registers off one served date (the design's rule):
 *
 *   quiet  8+ days   the habit-forming week stays a whisper
 *   mid    4–7 days  plain statement of fact
 *   clear  ≤3 days   the one moment the countdown is allowed to be loud
 *
 * The trial's length and end are SERVED (`trialStartedAt`/`trialEndsAt` on the
 * user doc, echoed by the callables) — 14 never appears as a client constant,
 * because `trialDays` is config (1–90). {@link trialSpanLabel} is the one
 * spelling of that length for every screen that states it.
 *
 * ## The takeover's numbers are measured, never invented
 *
 * `TrialSummary` counts arrive `null` when the server could not establish them,
 * and every caller drops the number (or the whole beat) rather than rendering a
 * zero nobody measured — the memoryCounts rule, applied to the one screen whose
 * entire argument is that 47 is a receipt where "a lot" is a claim.
 */

import { commitRememberedSelectionEurW, getTrialSummaryEurW } from '../firebaseConfig';
import { numOr0, numOrNull } from './rememberService';
import { toDateSafe } from '../utils/dateUtils';

const DAY_MS = 24 * 60 * 60 * 1000;

export type TrialRegister = 'quiet' | 'mid' | 'clear';

export interface TrialCountdown {
  isTrialing: boolean;
  daysLeft: number;
  register: TrialRegister;
  endsAt: Date | null;
}

const NOT_TRIALING: TrialCountdown = {
  isTrialing: false, daysLeft: 0, register: 'quiet', endsAt: null,
};

function registerOf(daysLeft: number): TrialRegister {
  return daysLeft <= 3 ? 'clear' : daysLeft <= 7 ? 'mid' : 'quiet';
}

/**
 * Countdown state off the fields the signed-in user doc already carries.
 *
 * Same trialing derivation as the premium page: status says a trial exists and
 * was not converted/expired, the clock says it has not run out, and a paid plan
 * wins outright — a subscriber is never counted down, whatever their trial
 * fields say (the sweep may not have stamped "converted" yet).
 */
export function trialCountdownOf(
  user: Record<string, unknown> | null | undefined,
  now: number = Date.now(),
): TrialCountdown {
  if (!user) return NOT_TRIALING;
  if (typeof user.selectedPlan === 'string' && user.selectedPlan !== '' && user.selectedPlan !== 'free') {
    return NOT_TRIALING;
  }
  if (user.trialStatus !== 'active') return NOT_TRIALING;
  const endsAt = toDateSafe(user.trialEndsAt as never);
  if (!endsAt || endsAt.getTime() <= now) return NOT_TRIALING;
  const daysLeft = Math.max(1, Math.ceil((endsAt.getTime() - now) / DAY_MS));
  return { isTrialing: true, daysLeft, register: registerOf(daysLeft), endsAt };
}

/** The chip's words, by register — one spelling for every surface that renders it. */
export function countdownLabel(c: TrialCountdown): string {
  const d = c.daysLeft;
  if (c.register === 'clear') return `${d} day${d === 1 ? '' : 's'} left`;
  if (c.register === 'mid') return `trial · ${d} days left`;
  return `trial · ${d} days`;
}

/* ------------------------------------------------------------------ *
 * The day-14 takeover
 * ------------------------------------------------------------------ */

/** One picker card's raw material — the server measures, this file formats. */
export interface TrialCandidate {
  personId: string;
  name: string;
  /** All-time memories — the card's depth signal. Null = not established. */
  things: number | null;
  lastAt: Date | null;
  dobDay?: string;
  dobMonth?: string;
}

export interface TrialSummary {
  hasTrial: boolean;
  /**
   * Server-resolved: the trial is actually OVER, for a still-free account.
   * False for a mid-trial caller, a converted payer (upgraded on another
   * device while this tab holds a stale snapshot), or a bot — the takeover
   * renders NOTHING when this is false.
   */
  eligible: boolean;
  /** Whether the takeover had already been seen BEFORE this fetch. */
  seen: boolean;
  /** Null when the payload is malformed — the caller must exit, never render 0 slots. */
  freeLimit: number | null;
  rememberedCount: number;
  learned: number | null;
  people: number | null;
  prepped: number | null;
  /** Totals are lower bounds — the candidate walk hit its cap. */
  truncated: boolean;
  trialStartedAt: Date | null;
  trialEndsAt: Date | null;
  candidates: TrialCandidate[];
}

/**
 * The trial's length, spoken: "two weeks" for 14 days, else "9 days" / "1 day".
 * Null when the dates cannot establish it — the caller drops the clause.
 */
export function trialSpanLabel(s: Pick<TrialSummary, 'trialStartedAt' | 'trialEndsAt'>): string | null {
  if (!s.trialStartedAt || !s.trialEndsAt) return null;
  const days = Math.round((s.trialEndsAt.getTime() - s.trialStartedAt.getTime()) / DAY_MS);
  if (days <= 0) return null;
  if (days === 14) return 'two weeks';
  return `${days} day${days === 1 ? '' : 's'}`;
}

const strOr = (v: unknown, fallback: string): string =>
  typeof v === 'string' && v ? v : fallback;

function parseSummary(d: Record<string, unknown>): TrialSummary {
  const rawCandidates = Array.isArray(d.candidates) ? d.candidates : [];
  const freeLimit = numOrNull(d.freeLimit);
  return {
    hasTrial: d.hasTrial === true,
    eligible: d.eligible === true,
    seen: d.seen === true,
    freeLimit: freeLimit !== null && freeLimit >= 1 ? freeLimit : null,
    rememberedCount: numOr0(d.rememberedCount),
    learned: numOrNull(d.learned),
    people: numOrNull(d.people),
    prepped: numOrNull(d.prepped),
    truncated: d.truncated === true,
    trialStartedAt: toDateSafe(d.trialStartedAt as never),
    trialEndsAt: toDateSafe(d.trialEndsAt as never),
    candidates: rawCandidates.map((raw) => {
      const c = (raw || {}) as Record<string, unknown>;
      const card: TrialCandidate = {
        personId: strOr(c.personId, ''),
        name: strOr(c.name, 'someone'),
        things: numOrNull(c.things),
        lastAt: toDateSafe(c.lastAt as never),
      };
      if (typeof c.dobDay === 'string' && typeof c.dobMonth === 'string') {
        card.dobDay = c.dobDay;
        card.dobMonth = c.dobMonth;
      }
      return card;
    }).filter((c) => c.personId),
  };
}

let summaryFetch: Promise<TrialSummary> | null = null;

/**
 * The receipt's numbers and the picker's cards, marking the takeover seen.
 *
 * Memoized for the session: the marker is exactly-once SERVER-side, so the
 * second call of a StrictMode double-mount (or a route-driven remount) must
 * share the first call's response rather than fetching again and reading its
 * own stamp back as "another device got there first" — which would make the
 * takeover never render in development and re-run the most expensive callable
 * in the feature on every shell re-entry.
 */
export function fetchTrialSummaryOnce(): Promise<TrialSummary> {
  if (!summaryFetch) {
    summaryFetch = getTrialSummaryEurW({ markSeen: true })
      .then((res) => parseSummary((res.data || {}) as Record<string, unknown>))
      .catch((err) => {
        // A failed fetch must not latch: the marker is only stamped on a
        // fully-assembled response, so the next mount may retry.
        summaryFetch = null;
        throw err;
      });
  }
  return summaryFetch;
}

export interface CommitResult {
  /** People whose state the commit actually changed. */
  changed: number;
  activeCount: number;
  /** Authoritative — the transaction's own read, not a driftable counter. */
  rememberedCount: number;
}

/**
 * Apply the picker's selection — one commit, never a partial state. The picker
 * must never write per tap; an abandoned flow then changes nothing at all.
 * Throws the raw callable error: `failed-precondition` means the choice no
 * longer applies (paid elsewhere, too many people) and the caller should exit;
 * `unavailable` means nothing changed and a retry is safe.
 */
export async function commitKeepSelection(keepPersonIds: string[]): Promise<CommitResult> {
  const res = await commitRememberedSelectionEurW({ keepPersonIds });
  const d = (res.data || {}) as Record<string, unknown>;
  return {
    changed: numOr0(d.changed),
    activeCount: numOr0(d.activeCount),
    rememberedCount: numOr0(d.rememberedCount),
  };
}
